import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@material-ui/core";

const PRIORITY_OPTIONS = [
  { value: "critical", label: "Critical", color: "#DC2626", bgColor: "#FEF2F2", description: "System down or critical failure" },
  { value: "high", label: "High", color: "#EA580C", bgColor: "#FFF7ED", description: "Major impact on operations" },
  { value: "medium", label: "Medium", color: "#0369A1", bgColor: "#EFF6FF", description: "Minor issue affecting workflow" },
  { value: "low", label: "Low", color: "#16A34A", bgColor: "#F0FDF4", description: "General inquiry or minor request" },
];

const AcknowledgeDialog = ({ open, agents = [], onClose, onConfirm }) => {
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentSearch, setAgentSearch] = useState("");

  const filteredAgents = agents.filter(
    (a) =>
      a.name?.toLowerCase().includes(agentSearch.toLowerCase()) ||
      a.email?.toLowerCase().includes(agentSearch.toLowerCase())
  );

  const handleConfirm = () => {
    if (selectedPriority && selectedAgent) {
      onConfirm(selectedPriority, selectedAgent);
      setSelectedPriority(null);
      setSelectedAgent(null);
      setAgentSearch("");
    }
  };

  const handleClose = () => {
    setSelectedPriority(null);
    setSelectedAgent(null);
    setAgentSearch("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
          Acknowledge Ticket
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#64748B", mt: 0.5 }}>
          Set the ticket priority and assign an agent to handle this ticket.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {/* Priority Selection */}
        <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1.5 }}>
          Priority
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 3 }}>
          {PRIORITY_OPTIONS.map((opt) => {
            const isSelected = selectedPriority === opt.value;
            return (
              <Box
                key={opt.value}
                onClick={() => setSelectedPriority(opt.value)}
                sx={{
                  padding: "12px",
                  borderRadius: "8px",
                  border: isSelected ? `2px solid ${opt.color}` : "1px solid #E2E8F0",
                  backgroundColor: isSelected ? opt.bgColor : "#fff",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": { backgroundColor: opt.bgColor },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: opt.color }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: opt.color }}>
                    {opt.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 11, color: "#64748B" }}>
                  {opt.description}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Agent Selection */}
        <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1.5 }}>
          Assign Agent
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search agents..."
          value={agentSearch}
          onChange={(e) => setAgentSearch(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <Box sx={{ maxHeight: 200, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: "8px" }}>
          <List dense disablePadding>
            {filteredAgents.map((agent) => {
              const isSelected = selectedAgent?.id === agent.id;
              return (
                <ListItem
                  key={agent.id}
                  button
                  onClick={() => setSelectedAgent(agent)}
                  sx={{
                    backgroundColor: isSelected ? "#EFF6FF" : "transparent",
                    borderLeft: isSelected ? "3px solid #0369A1" : "3px solid transparent",
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 13, backgroundColor: "#0369A1" }}>
                      {(agent.name || "?").charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={agent.name}
                    secondary={agent.email}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: isSelected ? 600 : 400 }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </ListItem>
              );
            })}
            {filteredAgents.length === 0 && (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography sx={{ fontSize: 13, color: "#64748B" }}>No agents found</Typography>
              </Box>
            )}
          </List>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none", color: "#64748B" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedPriority || !selectedAgent}
          sx={{
            textTransform: "none",
            backgroundColor: "#0369A1",
            "&:hover": { backgroundColor: "#025e8c" },
          }}
        >
          Acknowledge & Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AcknowledgeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  agents: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default AcknowledgeDialog;
